import { useEffect, useMemo, useState } from 'react';
import {
  subscribeDebug,
  clearDebugEntries,
  type DebugEntry,
  type DebugLevel,
} from '../lib/debugBus';
import { APP_ENV, APP_DEBUG, DEBUG_MODE, API_BASE_URL } from '../lib/api';

// On-screen debug console for the public website. Mounted only when DEBUG_MODE is
// on. Surfaces server errors, browser errors, unhandled rejections, console output
// and API/network failures so citizens' issues can be root-caused live.

const LEVEL_STYLES: Record<DebugLevel, { badge: string; text: string; label: string }> = {
  error: { badge: 'bg-red-500', text: 'text-red-300', label: 'ERROR' },
  warn: { badge: 'bg-amber-500', text: 'text-amber-300', label: 'WARN' },
  info: { badge: 'bg-blue-500', text: 'text-blue-300', label: 'INFO' },
  debug: { badge: 'bg-purple-500', text: 'text-purple-300', label: 'DEBUG' },
  log: { badge: 'bg-gray-500', text: 'text-gray-300', label: 'LOG' },
};

type Filter = 'all' | DebugLevel;

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString(undefined, { hour12: false }) + '.' + String(d.getMilliseconds()).padStart(3, '0');
}

function EntryRow({ entry }: { entry: DebugEntry }) {
  const [expanded, setExpanded] = useState(false);
  const style = LEVEL_STYLES[entry.level];
  const hasDetail = Boolean(entry.detail && entry.detail.trim());

  return (
    <div className="border-b border-gray-800 px-3 py-2 font-mono text-xs leading-relaxed">
      <div
        className={`flex items-start gap-2 ${hasDetail ? 'cursor-pointer' : ''}`}
        onClick={() => hasDetail && setExpanded((v) => !v)}
      >
        <span className={`mt-0.5 inline-block h-2 w-2 flex-shrink-0 rounded-full ${style.badge}`} aria-hidden />
        <span className="flex-shrink-0 text-gray-500">{formatTime(entry.ts)}</span>
        <span className={`flex-shrink-0 font-semibold ${style.text}`}>{style.label}</span>
        <span className="flex-shrink-0 rounded bg-gray-800 px-1.5 text-gray-400">{entry.source}</span>
        <span className="whitespace-pre-wrap break-words text-gray-200">{entry.message || '(empty)'}</span>
        {hasDetail && (
          <span className="ml-auto flex-shrink-0 text-gray-500">{expanded ? '▾' : '▸'}</span>
        )}
      </div>
      {expanded && hasDetail && (
        <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-black/60 p-3 text-[11px] text-green-300">
          {entry.detail}
        </pre>
      )}
    </div>
  );
}

export function DebugConsole() {
  const [entries, setEntries] = useState<DebugEntry[]>([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');

  useEffect(() => subscribeDebug(setEntries), []);

  const counts = useMemo(() => {
    let errors = 0;
    let warnings = 0;
    for (const e of entries) {
      if (e.level === 'error') errors++;
      else if (e.level === 'warn') warnings++;
    }
    return { errors, warnings };
  }, [entries]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries
      .filter((e) => (filter === 'all' ? true : e.level === filter))
      .filter((e) => (q ? (e.message + ' ' + (e.detail ?? '')).toLowerCase().includes(q) : true))
      .slice()
      .reverse();
  }, [entries, filter, query]);

  const copyAll = () => {
    const text = entries
      .map((e) => `[${formatTime(e.ts)}] ${e.level.toUpperCase()} (${e.source}) ${e.message}${e.detail ? '\n' + e.detail : ''}`)
      .join('\n\n');
    navigator.clipboard?.writeText(text).catch(() => undefined);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-[9998] flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 text-xs font-semibold text-white shadow-2xl ring-1 ring-white/10 hover:bg-gray-800"
        aria-label="Open debug console"
        title="Debug console (APP_DEBUG)"
      >
        <span className="inline-block h-2 w-2 rounded-full bg-green-400" aria-hidden />
        Debug
        {counts.errors > 0 && (
          <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] leading-none text-white">{counts.errors}</span>
        )}
        {counts.warnings > 0 && (
          <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] leading-none text-white">{counts.warnings}</span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-[9998] flex h-[70vh] max-h-[640px] w-[min(92vw,560px)] flex-col overflow-hidden rounded-xl bg-gray-950 text-white shadow-2xl ring-1 ring-white/10">
      <div className="flex items-center gap-2 border-b border-gray-800 bg-gray-900 px-3 py-2">
        <span className="inline-block h-2 w-2 rounded-full bg-green-400" aria-hidden />
        <span className="text-xs font-semibold">Debug Console</span>
        <span className="rounded bg-gray-800 px-1.5 py-0.5 text-[10px] text-gray-400">{APP_ENV}</span>
        <span className="rounded bg-green-900/60 px-1.5 py-0.5 text-[10px] text-green-300">APP_DEBUG {APP_DEBUG ? 'ON' : 'OFF'}</span>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={copyAll} className="rounded px-2 py-1 text-[11px] text-gray-300 hover:bg-gray-800" title="Copy all entries">Copy</button>
          <button type="button" onClick={clearDebugEntries} className="rounded px-2 py-1 text-[11px] text-gray-300 hover:bg-gray-800" title="Clear">Clear</button>
          <button type="button" onClick={() => setOpen(false)} className="rounded px-2 py-1 text-[11px] text-gray-300 hover:bg-gray-800" aria-label="Close debug console">✕</button>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-gray-800 bg-gray-900/60 px-3 py-2">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as Filter)}
          className="rounded bg-gray-800 px-2 py-1 text-[11px] text-gray-200 focus:outline-none"
          aria-label="Filter by level"
        >
          <option value="all">All ({entries.length})</option>
          <option value="error">Errors ({counts.errors})</option>
          <option value="warn">Warnings ({counts.warnings})</option>
          <option value="info">Info</option>
          <option value="debug">Debug</option>
          <option value="log">Log</option>
        </select>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search messages & stacks…"
          className="flex-1 rounded bg-gray-800 px-2 py-1 text-[11px] text-gray-200 placeholder-gray-500 focus:outline-none"
        />
      </div>

      <div className="flex-1 overflow-auto bg-gray-950">
        {visible.length === 0 ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-xs text-gray-500">
            No {filter === 'all' ? '' : filter + ' '}entries captured yet. Errors, console logs, and API/network
            failures will appear here.
          </div>
        ) : (
          visible.map((entry) => <EntryRow key={entry.id} entry={entry} />)
        )}
      </div>

      <div className="border-t border-gray-800 bg-gray-900 px-3 py-1.5 text-[10px] text-gray-500">
        API: {API_BASE_URL} · {entries.length} captured · DEBUG_MODE {DEBUG_MODE ? 'on' : 'off'}
      </div>
    </div>
  );
}
